// Minimal Rect/Offset with the semantics of dart:ui, so the layout planner
// ported from lib/Games/general/quick/twenty_challenge/twenty_challenge_layout.dart
// computes the same numbers as the app.

export class Offset {
  constructor(
    readonly dx: number,
    readonly dy: number,
  ) {}

  static readonly zero = new Offset(0, 0);

  plus(o: Offset): Offset {
    return new Offset(this.dx + o.dx, this.dy + o.dy);
  }

  minus(o: Offset): Offset {
    return new Offset(this.dx - o.dx, this.dy - o.dy);
  }

  times(f: number): Offset {
    return new Offset(this.dx * f, this.dy * f);
  }

  get distance(): number {
    return Math.sqrt(this.dx * this.dx + this.dy * this.dy);
  }

  equals(o: Offset): boolean {
    return this.dx === o.dx && this.dy === o.dy;
  }
}

export class Rect {
  constructor(
    readonly left: number,
    readonly top: number,
    readonly right: number,
    readonly bottom: number,
  ) {}

  static fromLTRB(l: number, t: number, r: number, b: number): Rect {
    return new Rect(l, t, r, b);
  }

  static fromLTWH(l: number, t: number, w: number, h: number): Rect {
    return new Rect(l, t, l + w, t + h);
  }

  static fromCenter(center: Offset, width: number, height: number): Rect {
    return new Rect(
      center.dx - width / 2,
      center.dy - height / 2,
      center.dx + width / 2,
      center.dy + height / 2,
    );
  }

  get width(): number {
    return this.right - this.left;
  }

  get height(): number {
    return this.bottom - this.top;
  }

  get center(): Offset {
    return new Offset(this.left + this.width / 2, this.top + this.height / 2);
  }

  get shortestSide(): number {
    return Math.min(Math.abs(this.width), Math.abs(this.height));
  }

  /// Like dart:ui: may come out with a negative width or height.
  intersect(o: Rect): Rect {
    return new Rect(
      Math.max(this.left, o.left),
      Math.max(this.top, o.top),
      Math.min(this.right, o.right),
      Math.min(this.bottom, o.bottom),
    );
  }

  overlaps(o: Rect): boolean {
    if (this.right <= o.left || o.right <= this.left) return false;
    if (this.bottom <= o.top || o.bottom <= this.top) return false;
    return true;
  }

  shift(o: Offset): Rect {
    return new Rect(this.left + o.dx, this.top + o.dy, this.right + o.dx, this.bottom + o.dy);
  }

  contains(p: Offset): boolean {
    return p.dx >= this.left && p.dx < this.right && p.dy >= this.top && p.dy < this.bottom;
  }
}

export function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}
